import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import { ListWorkOrdersResult } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { useDisclosure, useReportGenerate } from '@/hooks';
import { FILE_FORMAT } from '@/hooks/useFileLoading';
import { Button, Menu, Popover, Spin, Tooltip } from 'antd';
import { useState } from 'react';
import { PlusSquare } from 'react-feather';
import { useHistory } from 'react-router-dom';

import './styles.less';
import {
  useQuestReportWorkOrderCost,
  useQuestReportWorkOrderWorkshop,
} from '@/core/domain/questReport/WorkOrder/WorkOder.hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { buildParamsStr } from '@/utils/queryParams';
import ComplementAuthorizeModal from '../../Details/WorkOrderFooter/ComplementAuthorizeModal';
import formatterPrice from '@/utils/number/formatterPrice';
import WorkOrderReportAdministrativeModal from '../../WorkOrderReportAdministrativeModal';
import WorkOrderReportQuotationMapModal from '../../WorkOrderReportQuotationMapModal';
import WorkOrderReportAdministrativeSyntheticModal from '../../WorkOrderReportAdministrativeSyntheticModal';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import WorkOrderReportWorkOrderCostModal from '../../WorkOrderReportWorkOrderCostModal';
import ComplementClose from '../../Details/WorkOrderFooter/ComplementClose';
import ComplementCloseProvider from '../../Details/WorkOrderFooter/ComplementCloseModal/ComplementCloseProvider';

type Props = {
  workorder: ListWorkOrdersResult;
};

const WorkOrderActionDropdown: React.FC<Props> = ({ workorder }) => {
  const history = useHistory();
  const report = useReportGenerate();
  const [open, setOpen] = useState(false);

  const [confirmAuthorize, setConfirmAuthorize] = useDisclosure(false, {
    onOpen() {
      setOpen(false);
    },
  });

  const [modalQuotationMapOpened, modalQuotationMapAction] = useDisclosure();

  const [modalAdministrativeOpened, modalAdministrativeAction] =
    useDisclosure();

  const [
    modalAdministrativeSyntheticOpened,
    modalAdministrativeSyntheticAction,
  ] = useDisclosure();

  const [modalWorkOrderCostOpened, modalWorkOrderCostAction] = useDisclosure();

  const [closeModalOpened, closeModalAction] = useDisclosure(false, {
    onOpen() {
      setOpen(false);
    },
  });

  const reportOffice = useQuestReportWorkOrderWorkshop();
  const reportCost = useQuestReportWorkOrderCost();

  const reportAdminSynthetic = useCommonGenerateReport(
    ``,
    'Relatório gerado com sucesso!',
    'Relatório administrativo - Sintético gerado com sucesso.',
  );

  const reportInpectionList = useCommonGenerateReport(
    ``,
    'Relatório gerado com sucesso!',
    'Relatório lista de inspeção gerado com sucesso.',
  );

  async function generateReportOffice(header: boolean) {
    const result = await reportOffice.mutateAsync({
      complementId: workorder.complementId,
      header,
    });

    report.openFile({
      result,
      fileName: 'relatorio-para-oficina.pdf',
      fileType: 'application/pdf',
    });
  }

  async function generateReportInspectionList() {
    const result = await reportInpectionList.mutateAsync({
      workorderId: workorder.workOrderId,
      format: FILE_FORMAT.PDF,
      url: '/generate/work-order/inspection-list',
    });

    report.openFile({
      result,
      fileName: 'relatorio-lista-inspecao.pdf',
      fileType: 'application/pdf',
    });
  }

  async function generateReportAdminSynthetic() {
    const permission = hasPermissions([
      Authorities.MOVIMENTATION_WORKORDER_VIEW_COST_HOUR_ADMINISTRATIVE_BUDGET_REPORTS,
    ]);

    if (permission) {
      modalAdministrativeSyntheticAction.open();
    } else {
      if (!!workorder.workOrderId) {
        const result = await reportAdminSynthetic.mutateAsync({
          workOrderId: workorder.workOrderId,
          complementId: workorder.complementId,
          format: 'PDF',
          url: '/generate/work-order-synthetic',
        });

        report.openFile({
          result,
          fileName: 'relatorio-custo-os.pdf',
          fileType: 'application/pdf',
        });

        modalAdministrativeSyntheticAction.close();
      }
    }
  }

  const sellingAboveCreditLimit =
    !hasPermissions([
      Authorities.MOVIMENTATION_WORKORDER_MAKE_SALE_ABOVE_CREDIT_LIMIT,
    ]) &&
    (workorder.customer?.creditLimit || 0) !== 0 &&
    workorder.price > (workorder.customer?.creditLimit || 0);

  const hiddenAuthorize = [
    WorkOrderComplementStatus.Authorized,
    WorkOrderComplementStatus.Closed,
    WorkOrderComplementStatus.Cancelled,
  ].includes(workorder.status);

  const hiddenClose = workorder.status !== WorkOrderComplementStatus.Authorized;

  return (
    <>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        overlayClassName="workorder-action-dropdown"
        placement="bottomRight"
        showArrow={false}
        destroyTooltipOnHide
        content={
          <Menu
            mode="inline"
            style={{ width: 300 }}
            inlineIndent={16}
            subMenuOpenDelay={0}
            subMenuCloseDelay={0}
          >
            <Menu.Item
              hidden={hiddenAuthorize}
              key="autorize"
              onClick={setConfirmAuthorize.open}
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_AUTHORIZE,
                ]) || sellingAboveCreditLimit
              }
            >
              <Tooltip
                title={
                  sellingAboveCreditLimit ? (
                    <>
                      <p>
                        O orçamento está ultrapassando o limite de crédito do
                        cliente.
                      </p>

                      <p>
                        Limite de crédito:{' '}
                        {formatterPrice(workorder.customer?.creditLimit || 0)}
                      </p>
                    </>
                  ) : (
                    ''
                  )
                }
              >
                Autorizar
              </Tooltip>
            </Menu.Item>

            <Menu.Item
              hidden={hiddenClose}
              key="close"
              onClick={closeModalAction.open}
              disabled={
                !hasPermissions([Authorities.MOVIMENTATION_WORKORDER_CLOSE])
              }
            >
              <Tooltip
                title={
                  !hasPermissions([Authorities.MOVIMENTATION_WORKORDER_CLOSE])
                    ? 'Você não tem permissão para fechar a O.S.'
                    : ''
                }
              >
                Fechar O.S.
              </Tooltip>
            </Menu.Item>

            <Menu.Item
              key="sendMail"
              onClick={() =>
                history.push(
                  `/app/movements/workOrders/${workorder.workOrderId}/complements/${workorder.complementId}/emails`,
                )
              }
            >
              Enviar por e-mail
            </Menu.Item>

            {hasPermissions([
              Authorities.MOVIMENTATION_WORKORDER_VIEW_CHECKLIST,
            ]) && (
              <Menu.Item
                key="checklist"
                disabled={!workorder?.vehicleId}
                onClick={() =>
                  history.push(
                    `/app/movements/workorderChecklist/${workorder.workOrderId}/damages?checklistItemType=workorder&checklistItemId=${workorder.complementId}&workorderId=${workorder.workOrderId}`,
                  )
                }
              >
                <Tooltip
                  title={
                    !workorder?.vehicleId
                      ? 'Não há veículo vinculado'
                      : undefined
                  }
                >
                  <div>Checklist</div>
                </Tooltip>
              </Menu.Item>
            )}

            <Menu.Divider />

            <Menu.SubMenu
              title="Relatórios"
              disabled={
                !hasPermissions([Authorities.MOVIMENTATION_WORKORDER_PRINT])
              }
            >
              <Menu.Item
                style={{ paddingLeft: 16 }}
                disabled={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_PRINT_REPORT_FOR_WORKSHOP,
                  ]) ||
                  (reportOffice.isLoading && reportOffice.variables?.header)
                }
                key="reportForOffice"
                onClick={() => generateReportOffice(true)}
                icon={
                  reportOffice.isLoading &&
                  reportOffice.variables?.header && <Spin size="small" />
                }
              >
                Relatório para oficina
              </Menu.Item>

              <Menu.Item
                style={{ paddingLeft: 16 }}
                disabled={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_PRINT_REPORT_FOR_WORKSHOP,
                  ]) ||
                  (reportOffice.isLoading && !reportOffice.variables?.header)
                }
                key="reportForOfficeSynthetic"
                onClick={() => generateReportOffice(false)}
                icon={
                  reportOffice.isLoading &&
                  !reportOffice.variables?.header && <Spin size="small" />
                }
              >
                Relatório para oficina - Sintético
              </Menu.Item>

              <Menu.Item
                style={{ paddingLeft: 16 }}
                key="reportAdmin"
                onClick={modalAdministrativeAction.open}
                disabled={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_PRINT_ADMINISTRATIVE_REPORT,
                  ])
                }
              >
                Relatório administrativo
              </Menu.Item>

              <Menu.Item
                style={{ paddingLeft: 16 }}
                key="reportAdminSynthetic"
                disabled={
                  reportAdminSynthetic.isLoading ||
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_PRINT_ADMINISTRATIVE_REPORT,
                  ])
                }
                onClick={generateReportAdminSynthetic}
                icon={reportAdminSynthetic.isLoading && <Spin size="small" />}
              >
                Relatório administrativo - Sintético
              </Menu.Item>

              <Menu.Item
                style={{ paddingLeft: 16 }}
                key="inspecionList"
                disabled={reportInpectionList.isLoading}
                onClick={generateReportInspectionList}
                icon={reportInpectionList.isLoading && <Spin size="small" />}
              >
                Lista de inspeção
              </Menu.Item>

              <Menu.Item
                style={{ paddingLeft: 16 }}
                key="quotationMap"
                onClick={modalQuotationMapAction.open}
                disabled={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_PRINT_QUOTATION_MAP,
                  ])
                }
              >
                Mapa de cotação
              </Menu.Item>

              <Menu.Item
                style={{ paddingLeft: 16 }}
                onClick={modalWorkOrderCostAction.open}
                disabled={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_PRINT_REPORT_OF_COST,
                  ]) || reportCost.isLoading
                }
                key="costWorkorder"
                icon={reportCost.isLoading && <Spin size="small" />}
              >
                Custo da OS
              </Menu.Item>
            </Menu.SubMenu>

            <Menu.SubMenu title="Fichas de pátio">
              <Menu.Item
                style={{ paddingLeft: 16 }}
                onClick={() =>
                  window.open(
                    `/privateReports/mechanicsRecord/${
                      workorder.company.id
                    }?${buildParamsStr({
                      workOrderId: workorder.workOrderId,
                      complementId: workorder.complementId,
                    })}`,
                    '_blank',
                  )
                }
                key="mechanicsRecord"
              >
                Folha do mecânico
              </Menu.Item>

              <Menu.Item
                style={{ paddingLeft: 16 }}
                onClick={() =>
                  window.open(
                    `/privateReports/mechanicsRecord/${
                      workorder.company.id
                    }?${buildParamsStr({
                      workOrderId: workorder.workOrderId,
                      complementId: workorder.complementId,
                      isSynthetic: true,
                    })}`,
                    '_blank',
                  )
                }
                key="mechanicsRecordSynthetic"
              >
                Folha do mecânico sintética
              </Menu.Item>

              <Menu.Item
                style={{ paddingLeft: 16 }}
                onClick={() =>
                  window.open(
                    `/privateReports/inspectionRecord/${
                      workorder.company.id
                    }?${buildParamsStr({
                      workOrderId: workorder.workOrderId,
                    })}`,
                    '_blank',
                  )
                }
                key="inspectionRecord"
              >
                Ficha de inspeção técnica
              </Menu.Item>
            </Menu.SubMenu>
          </Menu>
        }
      >
        <Button icon={<PlusSquare size="21" />} type="link" />
      </Popover>

      <WorkOrderReportQuotationMapModal
        open={modalQuotationMapOpened}
        onCancel={modalQuotationMapAction.toggle}
        workOrderId={workorder?.workOrderId}
        complementNumber={workorder?.complementNumber}
      />

      <ComplementAuthorizeModal
        open={confirmAuthorize}
        onCancel={() => {
          setConfirmAuthorize.close();
        }}
        complementId={workorder.complementId}
        workorderId={workorder.workOrderId}
        workorderNumber={workorder.complementIdentifier}
        hasSignature={!!workorder.complementAuthorizedSignature}
        hasPecaAiBasket={!!workorder?.pecaAiBasketId}
      />

      <WorkOrderReportAdministrativeModal
        open={modalAdministrativeOpened}
        onCancel={modalAdministrativeAction.close}
        workOrderId={workorder.workOrderId}
        complementNumber={workorder.complementNumber}
        licensePlate={workorder?.licensePlate}
        complementIdentifier={workorder?.complementIdentifier}
      />

      <WorkOrderReportAdministrativeSyntheticModal
        open={modalAdministrativeSyntheticOpened}
        onCancel={modalAdministrativeSyntheticAction.close}
        workOrderId={workorder.workOrderId}
        complementId={workorder.complementId}
        licensePlate={workorder?.licensePlate}
        complementIdentifier={workorder?.complementIdentifier}
      />

      <WorkOrderReportWorkOrderCostModal
        open={modalWorkOrderCostOpened}
        onCancel={modalWorkOrderCostAction.close}
        workOrderId={workorder.workOrderId}
        complementNumber={workorder.complementNumber}
        licensePlate={workorder?.licensePlate}
        complementIdentifier={workorder?.complementIdentifier}
      />

      {closeModalOpened && (
        <ComplementCloseProvider
          workOrderId={workorder.workOrderId}
          complementId={workorder.complementId}
        >
          <ComplementClose
            open={closeModalOpened}
            onClose={closeModalAction.close}
          />
        </ComplementCloseProvider>
      )}
    </>
  );
};

export default WorkOrderActionDropdown;
